import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { Controller, UseFormReturn } from 'react-hook-form';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import {
  FormNewDataSourceData,
  PageBody,
  PageButtonContainer,
} from 'dashboard/forms/FormNewDataSource/types';
import { S3Config, S3Object } from 'dashboard/models/external-integration';
import Button from 'design-system/components/Button';
import Checkbox from 'verify/components/Checkbox';

const InputLabel = styled('p')`
  ${Typography.TextMedium};
  padding-bottom: ${Spacing[1]};
`;

const NameLabel = styled(`h3`)`
  ${Typography.TextMedium};
  padding-bottom: ${Spacing[2]};
`;

const InfoLabel = styled('h4')`
  ${Typography.TextMedium};
  padding-bottom: ${Spacing[1]};
`;

const LineDivider = styled.div`
  padding-top: ${Spacing[3]};
  border-top: 1px solid ${Color.CoreSlate100};
`;

const SaveButtonContainer = styled.div`
  width: 100%;
`;

const BackButton = styled(Button)`
  width: fit-content;
`;

const SaveButton = styled(Button)`
  width: fit-content;
  float: right;
`;

const ObjectKeyContainer = styled.div`
  overflow-y: scroll;
  padding: ${Spacing[3]};
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  min-height: 100px;
  max-height: 400px;
`;

const ObjectKeyCheckbox = styled(Checkbox)`
  padding-bottom: ${Spacing[1]};
`;

interface SelectS3ObjectPageProps {
  form: UseFormReturn<FormNewDataSourceData>;
  s3Config: S3Config;
  s3Objects: S3Object[];
  onBack: () => void;
  onSubmit: () => void;
}

const SelectS3ObjectPage = ({
  form,
  s3Objects,
  s3Config,
  onBack,
  onSubmit,
}: SelectS3ObjectPageProps) => {
  const {
    control,
    formState: { isDirty, isSubmitting, isValid },
    watch,
  } = form;

  const selectedObjectKeys = watch('objectKeys') || [];
  const region = watch('region');
  const regionName = s3Config.available_regions.find((r) => r.value === region)?.label;

  return (
    <div>
      <PageBody>
        <NameLabel>{watch('name')}:</NameLabel>
        <InfoLabel>Bucket: {watch('bucket')}</InfoLabel>
        <InfoLabel>Region: {regionName}</InfoLabel>
        <LineDivider />
        <InputLabel>{selectedObjectKeys.length} Objects Selected: </InputLabel>
        <ObjectKeyContainer>
          <Controller
            control={control}
            name='objectKeys'
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => {
              return (
                <div>
                  {s3Objects.map((obj) => {
                    const key = obj.key;
                    return (
                      <ObjectKeyCheckbox
                        key={`s3-object-checkbox-${key}`}
                        id={`s3-object-checkbox-${key}`}
                        label={key}
                        value={(value || []).includes(key)}
                        onChange={(includeKey: boolean) => {
                          const selectedKeys = value || [];
                          if (includeKey) {
                            onChange([...new Set([...selectedKeys, key])]);
                          } else {
                            onChange(selectedKeys.filter((k) => k !== key));
                          }
                        }}
                      />
                    );
                  })}
                </div>
              );
            }}
          />
        </ObjectKeyContainer>
      </PageBody>
      <PageButtonContainer>
        <BackButton
          id='form-new-data-source__select-s3-object-page_back-button'
          disabled={isSubmitting}
          variant='basic'
          onClick={onBack}
        >
          Back
        </BackButton>
        <SaveButtonContainer>
          <SaveButton
            id='form-new-data-source__select-s3-object-page_save-button'
            type='button'
            disabled={isSubmitting || !isDirty || !isValid}
            isPending={isSubmitting}
            onClick={onSubmit}
          >
            Create
          </SaveButton>
        </SaveButtonContainer>
      </PageButtonContainer>
    </div>
  );
};

export default SelectS3ObjectPage;
